import { propertyOperation, propertyFetch } from '@/lib/property-api'
import { propertyApi } from '@/lib/property-api'

export type PropertyArticle = {
  id: string
  uid: string | null
  profileId: string | null
  profileName: string | null
  title: string
  summary: string
  content: string
  coverImageUrl: string | null
  sourceNewsTitle: string | null
  sourceNewsUrl: string | null
  sourceContentType: string | null
  status: string
  readCount: number
  lastReadAt: string | null
  createdAt: string | null
  updatedAt: string | null
}

type PublicBlogsListPayload = {
  offset?: number
  q?: string
  limit?: number
  cursorUpdatedAt?: string | null
  cursorId?: string | null
}

type PublicBlogsListResult = {
  total?: number
  nextOffset?: number | null
  success?: boolean
  items?: unknown[]
  hasMore?: boolean
  nextCursorUpdatedAt?: string | null
  nextCursorId?: string | null
}

type PublicBlogReadTrackPayload = {
  blogId: string
}

type PublicBlogReadTrackResult = {
  success?: boolean
  blogId?: string
  readCount?: number
  lastReadAt?: string | null
}

export type PropertyArticlesListResult = {
  total: number
  nextOffset: number | null
  items: PropertyArticle[]
  hasMore: boolean
  nextCursorUpdatedAt: string | null
  nextCursorId: string | null
}

const publicBlogsListCallable = propertyOperation<PublicBlogsListPayload, PublicBlogsListResult>(
  propertyApi,
  'socialPublicBlogsList',
)
const publicBlogReadTrackCallable = propertyOperation<PublicBlogReadTrackPayload, PublicBlogReadTrackResult>(
  propertyApi,
  'socialPublicBlogReadTrack',
)

function normalizeString(value: unknown) {
  return typeof value === 'string' ? value.trim() : ''
}

function normalizeNumber(value: unknown) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function normalizeDate(value: unknown) {
  if (typeof value === 'string' && value.trim()) {
    return value.trim()
  }
  return null
}

function normalizeArticle(value: unknown): PropertyArticle {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
  return {
    id: normalizeString(source.id),
    uid: normalizeString(source.uid) || null,
    profileId: normalizeString(source.profileId) || null,
    profileName: normalizeString(source.profileName) || null,
    title: normalizeString(source.title) || normalizeString(source.sourceNewsTitle) || 'Artikel properti',
    summary: normalizeString(source.summary),
    content: normalizeString(source.content),
    coverImageUrl: normalizeString(source.coverImageUrl) || null,
    sourceNewsTitle: normalizeString(source.sourceNewsTitle) || null,
    sourceNewsUrl: normalizeString(source.sourceNewsUrl) || null,
    sourceContentType: normalizeString(source.sourceContentType) || null,
    status: normalizeString(source.status) || 'ready',
    readCount: Math.max(0, Math.floor(normalizeNumber(source.readCount))),
    lastReadAt: normalizeDate(source.lastReadAt),
    createdAt: normalizeDate(source.createdAt),
    updatedAt: normalizeDate(source.updatedAt),
  }
}

export function propertyArticleSlugText(value: string) {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' dan ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 96)
}

export function getPropertyArticleSlug(article: Pick<PropertyArticle, 'id' | 'title' | 'sourceNewsTitle'>) {
  const titleSeed = article.title || article.sourceNewsTitle || 'artikel-properti'
  const slug = propertyArticleSlugText(titleSeed) || 'artikel-properti'
  return `${slug}--${article.id}`
}

export function getPropertyArticleIdFromSlug(slug: string) {
  const normalized = decodeURIComponent(String(slug || '')).trim()
  const parts = normalized.split('--')
  return parts.length > 1 ? parts[parts.length - 1].trim() : ''
}

export function estimatePropertyArticleReadMinutes(content: string) {
  const words = content.trim().split(/\s+/).filter(Boolean).length
  return Math.max(1, Math.ceil(words / 190))
}

export function formatPropertyArticleDate(value: string | null) {
  if (!value) return 'Baru diperbarui'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Baru diperbarui'
  return new Intl.DateTimeFormat('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

export async function listPropertyArticles(payload: PublicBlogsListPayload = {}): Promise<PropertyArticlesListResult> {
  const result = await publicBlogsListCallable({ ...payload, limit: payload.limit ?? 20 })
  const data = result.data
  return {
    items: (data.items || []).map(normalizeArticle),
    total: data.total || 0,
    nextOffset: data.nextOffset ?? null,
    hasMore: data.hasMore === true,
    nextCursorUpdatedAt: data.nextCursorUpdatedAt || null,
    nextCursorId: data.nextCursorId || null,
  }
}

export async function getPropertyArticle(id: string): Promise<PropertyArticle> {
  const { item } = await propertyFetch<{item: unknown}>(`/v1/property/articles/${encodeURIComponent(id)}`, {public: true})
  return normalizeArticle(item)
}

export async function trackPropertyArticleRead(blogId: string) {
  const result = await publicBlogReadTrackCallable({ blogId })
  return {
    success: Boolean(result.data?.success),
    blogId: normalizeString(result.data?.blogId),
    readCount: Math.max(0, Math.floor(normalizeNumber(result.data?.readCount))),
    lastReadAt: normalizeDate(result.data?.lastReadAt),
  }
}
